import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, Spinner, Table } from 'react-bootstrap';
import StatusBadge from '../../components/StatusBadge';
import accountApi from '../../services/accountApi';

function PlatformUserDetailPage() {
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await accountApi.getPlatformUser(userId);
        if (!cancelled) setUser(data);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load user');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  return (
    <div>
      <p className="mb-2">
        <Link to="/platform/users">← Users</Link>
      </p>
      <h1 className="product-page-title">User detail</h1>
      <p className="product-page-subtitle">
        Bounded account, membership, and platform-role summary.
      </p>

      {error && <Alert variant="danger">{error}</Alert>}
      {loading && (
        <div className="loading-state">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading…
        </div>
      )}

      {user && (
        <>
          <div className="product-card mb-3">
            <p className="mb-1">
              <strong>Email:</strong> {user.email}
            </p>
            <p className="mb-1">
              <strong>Name:</strong> {user.display_name || '—'}
            </p>
            <p className="mb-1">
              <strong>Status:</strong>{' '}
              <StatusBadge status={user.is_active ? 'active' : 'suspended'} />
            </p>
            <p className="mb-1">
              <strong>Last login:</strong>{' '}
              {user.last_login ? new Date(user.last_login).toLocaleString() : '—'}
            </p>
            <p className="mb-0">
              <strong>Platform roles:</strong>{' '}
              {(user.platform_roles || []).map((r) => r.role || r).join(', ') || '—'}
            </p>
          </div>

          <div className="product-card mb-3">
            <h2 className="h6">Partner memberships</h2>
            <Table responsive className="product-table" size="sm">
              <thead>
                <tr>
                  <th>Partner</th>
                  <th>Role</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {(user.memberships || []).length === 0 ? (
                  <tr>
                    <td colSpan={3} className="text-muted">
                      No partner memberships.
                    </td>
                  </tr>
                ) : (
                  user.memberships.map((m) => (
                    <tr key={m.id}>
                      <td>
                        <Link to={`/platform/partners/${m.partner_id}`}>
                          {m.partner_name}
                        </Link>
                        <div className="mono small text-muted">{m.partner_slug}</div>
                      </td>
                      <td>{m.role}</td>
                      <td>
                        <StatusBadge status={m.status} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          <div className="product-card mb-3">
            <h2 className="h6">Pending invitations</h2>
            {(user.pending_invitations || []).length === 0 ? (
              <p className="mb-0 text-muted">None</p>
            ) : (
              <ul className="mb-0">
                {user.pending_invitations.map((i) => (
                  <li key={i.id}>
                    {i.partner_name} · {i.role}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="product-card">
            <h2 className="h6">Recent actor audit</h2>
            <Table responsive className="product-table" size="sm">
              <thead>
                <tr>
                  <th>Action</th>
                  <th>Partner</th>
                  <th>When</th>
                </tr>
              </thead>
              <tbody>
                {(user.recent_audit || []).length === 0 ? (
                  <tr>
                    <td colSpan={3} className="text-muted">
                      No recent events.
                    </td>
                  </tr>
                ) : (
                  user.recent_audit.map((e) => (
                    <tr key={e.id}>
                      <td className="mono">{e.action}</td>
                      <td className="mono">{e.partner_id?.slice?.(0, 8) || '—'}</td>
                      <td>{e.created_at ? new Date(e.created_at).toLocaleString() : '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}

export default PlatformUserDetailPage;
